import {
  authControllerLogin,
  authControllerRefreshToken,
  AuthType,
  customFetch,
  authControllerRefreshPin,
  UnauthorizedErrorType,
  useAcquisitionStoreServer,
  useAuthControllerLoginPin,
  UserRole,
  UserRoleName,
  type ErrorUnauthorizedDto,
  type SessionUserDto,
} from '@konsolenkost-de/acquisition_store_server_sdk_vue';
import { useQueryClient } from '@tanstack/vue-query';
import { defineStore } from 'pinia';
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { usePiniaRefStore } from '../composables/usePiniaRefStore';
import { usePosRequest } from '../composables/usePosRequest';
import {
  ADMIN_ROLES,
  BETA_TESTER_ROLES,
  EXTERNAL_ROLES,
  HANDLING_REVIEW_ROLES,
  HANDLING_ROLES,
  isSelfHandledErrorUrl,
  OFFER_REVIEW_ROLES,
  READONLY_ROLES,
  RESTRICTED_ROLES,
} from '../constants';
import { useLoading } from './loadingStore';
import { usePos, usePosStore } from './posStore';

interface UseAuthOptions {
  global?: boolean;
  /** `isPosMode` is true when a checked-in device is still active, i.e. a user
   *  switch rather than a full sign-out. Cache-owning listeners key off it to
   *  keep the basket/offers across PIN logins but wipe them on a real logout. */
  onLogin?: (user: SessionUserDto, isPosMode: boolean) => void;
  onLogout?: (userId: number, isPosMode: boolean) => void;
}

const { VITE_API_BASE_URL } = import.meta.env;

/** PIN login returns role *names*; the store keeps numeric roles. Keys match 1:1. */
const toUserRoles = (roleNames?: UserRoleName[]): UserRole[] =>
  (roleNames ?? [])
    .map((name) => UserRole[name as keyof typeof UserRole])
    .filter((role): role is UserRole => role !== undefined);

export const useAuthStore = defineStore(
  'auth',
  () => {
    const queryClient = useQueryClient();
    const { loading, watchLoading } = useLoading();
    const { setHeaders, addResponseInterceptor } = useAcquisitionStoreServer();
    const router = useRouter();
    const posStore = usePosStore();
    const { posRequest } = usePosRequest();

    // `networkMode: 'always'` because a PIN login has to go out while the online
    // manager is paused - a checked-in device waiting for a user is exactly
    // that state (see App.vue) and would otherwise suspend the mutation
    // indefinitely. `posRequest` supplies the device bearer at call time.
    const { mutateAsync: loginPin } = useAuthControllerLoginPin({
      mutation: { networkMode: 'always' },
      request: posRequest,
    });

    /** One PIN login attempt. Network failures come back as `null`. */
    const requestPinLogin = (pin: string) => loginPin({ data: { pin } }).catch(() => null);

    // Tracks the whole PIN flow including the device-refresh retry, which the
    // mutation's own `isPending` would drop between the two attempts.
    const isPinLoginPending = ref(false);

    let isRefreshingToken: Promise<void> | null = null;
    const userId = ref(0);
    const username = ref('');
    const roles = ref<UserRole[]>([]);
    const authToken = ref('');
    const refreshToken = ref('');
    const expiresAt = ref<Date | null>(null);
    const refreshExpiresAt = ref<Date | null>(null);
    /** Which credential opened the current session. Drives refresh + logout routing. */
    const authType = ref<AuthType>(AuthType.USER);

    const isLoggedIn = computed(() => !!userId.value);
    const isPinSession = computed(() => authType.value === AuthType.PIN);
    const canRefresh = computed(
      () =>
        isLoggedIn.value &&
        !!refreshToken.value &&
        (!refreshExpiresAt.value || new Date() < refreshExpiresAt.value),
    );
    const loginError = ref<string | null>(null);

    const isOfferReviewer = computed(() =>
      roles.value.some((role) => OFFER_REVIEW_ROLES.includes(role)),
    );
    const isHandlingReviewer = computed(() =>
      roles.value.some((role) => HANDLING_REVIEW_ROLES.includes(role)),
    );
    const isAdmin = computed(() => roles.value.some((role) => ADMIN_ROLES.includes(role)));
    const isHandling = computed(() => roles.value.some((role) => HANDLING_ROLES.includes(role)));
    const isRestricted = computed(() =>
      roles.value.some((role) => RESTRICTED_ROLES.includes(role)),
    );
    const isBetaTester = computed(() =>
      roles.value.some((role) => BETA_TESTER_ROLES.includes(role)),
    );
    const isReadonly = computed(() => roles.value.some((role) => READONLY_ROLES.includes(role)));
    const isExternal = computed(() => roles.value.some((role) => EXTERNAL_ROLES.includes(role)));
    const showPinDialog = computed(
      () => posStore.isPosMode && !isLoggedIn.value && !isPinSession.value,
    );

    const loginHandlers = ref<((user: SessionUserDto, isPosMode: boolean) => void)[]>([]);
    const logoutHandlers = ref<((userId: number, isPosMode: boolean) => void)[]>([]);

    const login = async (loginUsername: string, loginPassword: string) => {
      loading.value = true;

      const { status, data } = await authControllerLogin({
        username: loginUsername,
        password: loginPassword,
      })
        .catch(() => {
          return { status: 500, data: null };
        })
        .finally(() => {
          loading.value = false;
        });

      if (status === 404) {
        loginError.value = 'login-user-not-found';
        throw new Error('User not found');
      } else if (status === 401) {
        loginError.value = 'login-incorrect-password';
        throw new Error('Incorrect password');
      } else if (status !== 200 || !data) {
        loginError.value = 'login-failed';
        throw new Error('Login failed');
      }

      userId.value = data.user.userId;
      username.value = data.user.username;
      roles.value = data.user.roles ?? [];
      authToken.value = data.accessToken;
      refreshToken.value = data.refreshToken;
      expiresAt.value = new Date(data.expiresAt);
      refreshExpiresAt.value = new Date(data.refreshTokenExpiresAt);
      authType.value = data.user.authType ?? AuthType.USER;

      setHeaders({ Authorization: `Bearer ${data.accessToken}` });

      const isPosMode = usePosStore().isPosMode;
      await Promise.all(loginHandlers.value.map((handler) => handler(data.user, isPosMode)));
    };

    /**
     * Stage 2 of POS mode: a user identifies themselves with a PIN. The POS
     * device token is the bearer for this one call; the PIN tokens it returns
     * become the ordinary app session afterwards.
     */
    const pinLogin = async (pin: string) => {
      // One attempt at a time. The keypad auto-submits on `complete`, and a
      // re-fired complete (or a keypad holding down enter) would otherwise
      // hammer the endpoint with the same PIN.
      if (isPinLoginPending.value) return;

      loginError.value = null;
      isPinLoginPending.value = true;

      try {
        let result = await requestPinLogin(pin);

        // A 401 is ambiguous: the PIN may be wrong, or the device's POS access
        // token may have quietly expired since the last user used it. Rotate
        // the device token once and replay the attempt before blaming the
        // user - if it comes back 401 again, the PIN really is wrong.
        if (result?.status === 401 && posStore.canRefreshPos) {
          const refreshed = await posStore
            .refreshPosToken()
            .then(() => true)
            .catch(() => false);

          // `refreshPosToken` already dropped POS mode and routed to the
          // password login; a PIN can't revive a dead device.
          if (!refreshed) {
            loginError.value = null;
            throw new Error('POS device rejected');
          }

          result = await requestPinLogin(pin);
        }

        if (result?.status === 401 || result?.status === 404) {
          loginError.value = 'pin-login-invalid';
          throw new Error('Invalid PIN');
        } else if (result?.status === 403) {
          // The device token itself is dead - a PIN can't revive it, only a
          // manager checking the device back in. Drop POS mode and send them to
          // the password login with a notice rather than leaving a dead keypad
          // up.
          loginError.value = null;
          await posStore.exitPosMode(true);
          throw new Error('POS device rejected');
        } else if (result?.status !== 200) {
          loginError.value = 'pin-login-failed';
          throw new Error('PIN login failed');
        }

        const { data } = result;

        const sessionUser: SessionUserDto = {
          authType: AuthType.PIN,
          userId: data.user.id,
          username: data.user.username,
          roles: toUserRoles(data.user.roles),
          availableSourceIds: (data.user.availableSources ?? []).map((source) => source.id),
        };

        userId.value = sessionUser.userId;
        username.value = sessionUser.username;
        roles.value = sessionUser.roles;
        authToken.value = data.accessToken;
        refreshToken.value = data.refreshToken;
        expiresAt.value = new Date(data.expiresAt);
        refreshExpiresAt.value = new Date(data.refreshTokenExpiresAt);
        authType.value = AuthType.PIN;

        setHeaders({ Authorization: `Bearer ${data.accessToken}` });

        const isPosMode = usePosStore().isPosMode;
        await Promise.all(loginHandlers.value.map((handler) => handler(sessionUser, isPosMode)));
      } finally {
        isPinLoginPending.value = false;
      }
    };

    const refreshLogin = () => {
      if (isRefreshingToken) return isRefreshingToken;

      isRefreshingToken = new Promise(async (resolve, reject) => {
        try {
          // A PIN session refreshes at the PIN endpoint; everything else at the
          // ordinary one. Both take the refresh token as bearer and return 201.
          const refresh = isPinSession.value
            ? authControllerRefreshPin
            : authControllerRefreshToken;

          const { status, data } = await refresh({
            headers: { Authorization: `Bearer ${refreshToken.value}` },
          });

          if (status !== 201) {
            await logout();
            return reject(new Error('Failed to refresh token'));
          }

          authToken.value = data.accessToken;
          refreshToken.value = data.refreshToken;
          expiresAt.value = new Date(data.expiresAt);
          refreshExpiresAt.value = new Date(data.refreshTokenExpiresAt);

          setHeaders({ Authorization: `Bearer ${data.accessToken}` });
        } catch (error) {
          await logout();
          reject(error);
        } finally {
          resolve(void (isRefreshingToken = null));
        }
      });

      return isRefreshingToken;
    };

    /**
     * @param redirect Where to land after signing back in. Pass `''` for an
     *   intentional sign-out (logout button, POS handover) that should start
     *   fresh; omit it to fall back to the current page, which is what an
     *   automatic sign-out on token expiry wants.
     */
    const logout = async (redirect?: string | false) => {
      const posStore = usePosStore();
      // A checked-in device keeps its POS token through a sign-out, so only the
      // person needs to re-authenticate: send them to the keypad, not the
      // password form. This also covers the manager handing a freshly
      // registered device over to the first user.
      const isPosMode = posStore.isPosMode;

      await Promise.all(logoutHandlers.value.map((handler) => handler(userId.value, isPosMode)));

      userId.value = 0;
      username.value = '';
      authToken.value = '';
      refreshToken.value = '';
      expiresAt.value = null;
      refreshExpiresAt.value = null;
      authType.value = AuthType.USER;
      await nextTick();

      // A user stepping off a still-checked-in device is only a session swap,
      // so keep the cached basket/offers for the next PIN login. Any other
      // logout is a real sign-out and must not leak data into the next session.
      if (!isPosMode) queryClient.clear();

      if (redirect === false) return; // caller wants to stay on the current page

      // Never bounce the user back into an auth screen (/logout, /login, the
      // keypad) after they sign back in.
      const current = router.currentRoute.value;
      const target =
        redirect ??
        (current.meta.public || current.name === 'logout' ? undefined : current.fullPath);

      router.replace({
        name: isPosMode ? 'pinLogin' : 'login',
        query: target ? { redirect: target } : {},
        force: true,
      });
    };

    // A device checking out ends the person's session with it: whoever is
    // standing at the device only ever got in through the device. `exitPosMode`
    // has already cleared the POS state by the time this runs, so `logout`
    // correctly treats it as a real sign-out and not a user swap.
    //
    // Registered here rather than at the top of the store because the handler
    // needs `logout` to exist; `global` skips the component lifecycle, which a
    // store has no access to anyway.
    usePos({
      global: true,
      onPosEnd: async (_sourceId, isExpired) => {
        if (!isExpired) return logout(''); // deliberate check-out - start fresh

        // The device died under the user's hands. Sign out without routing, then
        // land on the password form with the notice `LoginView` renders off
        // `posExpired`.
        await logout(false);
        router.replace({ name: 'login', query: { posExpired: '1' }, force: true });
      },
    });

    const bootstrap = () => {
      addResponseInterceptor(async (url, request, response) => {
        // PIN login and POS refresh own their failure handling: a 401 there is a
        // wrong PIN or a dead device, neither of which a session refresh or the
        // forbidden page would help with.
        if (isSelfHandledErrorUrl(url)) return response;

        const data = await response
          .clone()
          .json()
          .catch(() => null);
        const canRefresh =
          isLoggedIn.value &&
          !!refreshToken.value &&
          (!refreshExpiresAt.value || new Date() < new Date(refreshExpiresAt.value));

        switch (response.status) {
          case 401:
            if (
              canRefresh &&
              (data as ErrorUnauthorizedDto)?.unauthorizedType ===
                UnauthorizedErrorType.TOKEN_INVALID &&
              !url.endsWith('auth/refresh-token') &&
              !url.endsWith('/auth/pin/refresh') &&
              !url.endsWith('/push-notification/unsubscribe')
            ) {
              await refreshLogin();
              const baseUrl = VITE_API_BASE_URL.startsWith('http')
                ? new URL(VITE_API_BASE_URL)
                : new URL(window.location.origin, VITE_API_BASE_URL);
              const relativeUrl = url.startsWith(baseUrl.href)
                ? url.slice(baseUrl.href.length)
                : url;
              return customFetch(relativeUrl, request);
            }
            break;
          case 403:
            router.replace({ name: 'forbidden' });
            break;
        }
        return response;
      });
    };

    watchLoading(isPinLoginPending);

    return {
      userId,
      username,
      roles,
      authToken,
      refreshToken,
      isLoggedIn,
      isPinSession,
      authType,
      canRefresh,
      loginError,
      isOfferReviewer,
      isHandlingReviewer,
      isAdmin,
      isHandling,
      isRestricted,
      isBetaTester,
      isReadonly,
      isExternal,
      expiresAt,
      refreshExpiresAt,
      loginHandlers,
      logoutHandlers,
      isPinLoginPending,
      showPinDialog,
      login,
      pinLogin,
      logout,
      refreshLogin,
      bootstrap,
    };
  },
  {
    persist: {
      omit: [
        'loginError',
        'isLoggedIn',
        'isPinSession',
        'isOfferReviewer',
        'isHandlingReviewer',
        'isAdmin',
        'isHandling',
        'isRestricted',
        'isBetaTester',
        'isReadonly',
        'isExternal',
        'canRefresh',
        'isPinLoginPending',
        'showPinDialog',
        'loginHandlers',
        'logoutHandlers',
      ],
      serializer: {
        serialize: JSON.stringify,
        deserialize: (data) => {
          const parsed = JSON.parse(data);
          if (parsed.expiresAt)
            parsed.expiresAt = parsed.expiresAt ? new Date(parsed.expiresAt) : null;
          if (parsed.refreshExpiresAt)
            parsed.refreshExpiresAt = parsed.expiresAt ? new Date(parsed.refreshExpiresAt) : null;

          // Invalidate session if refresh token is expired
          if (parsed.refreshExpiresAt && new Date() >= parsed.refreshExpiresAt) {
            parsed.userId = 0;
            parsed.username = '';
            parsed.authToken = '';
            parsed.refreshToken = '';
            parsed.expiresAt = null;
            parsed.refreshExpiresAt = null;
            parsed.authType = AuthType.USER;
          }

          return parsed;
        },
      },
      afterHydrate: (context) => {
        const store = context.store as ReturnType<typeof useAuthStore>;
        const { setHeaders } = useAcquisitionStoreServer();

        if (store.authToken) setHeaders({ Authorization: `Bearer ${store.authToken}` });
      },
    },
  },
);

export const useAuth = (options?: UseAuthOptions) => {
  const { loginHandlers, logoutHandlers, ...store } = usePiniaRefStore(useAuthStore());

  const register = () => {
    if (options?.onLogin) loginHandlers.value.push(options.onLogin);
    if (options?.onLogout) logoutHandlers.value.push(options.onLogout);
  };
  const unregister = () => {
    if (options?.onLogin)
      loginHandlers.value = loginHandlers.value.filter((handler) => handler !== options.onLogin);
    if (options?.onLogout)
      logoutHandlers.value = logoutHandlers.value.filter((handler) => handler !== options.onLogout);
  };

  if (options?.global) {
    register();
  } else {
    onMounted(register);
    onUnmounted(unregister);
  }

  return store;
};
